import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { Pagination } from '../components/Pagination';
import { PromptFilterBar } from '../components/PromptFilterBar';
import { PromptList } from '../components/PromptList';
import { PromptModerationDialog } from '../components/PromptModerationDialog';
import { TranscribeDialog } from '../components/TranscribeDialog';
import { usePausePrompt, useResumePrompt } from '../api';
import {
  useApprovePrompt,
  useBulkDeletePrompts,
  useDeletePrompt,
  usePendingPromptList,
  useRejectPrompt,
} from '../api';
import { downloadPromptAudio } from '../download';
import { sortPromptsNewestFirst } from '../sort';
import type { PromptCategory, PromptRow } from '../types';

export function AllVoiceFilesPage() {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canModerate = usePermission('Voice Library.Prompts for Approval', BASE_PERMISSION.EDIT);
  const canDeletePending = usePermission('Voice Library.Prompts for Approval', BASE_PERMISSION.DELETE);
  const canTranscribe = usePermission('Transcription', BASE_PERMISSION.VIEW);
  const [category, setCategory] = useState<'all' | PromptCategory>('all');
  const [query, setQuery] = useState('');
  const list = usePendingPromptList();

  /* The endpoint returns everything; admin view legacy filters to status
     === 0 (i.e. still-pending review). Category filter is client-side
     because the server's `remarks` body is a free-text filter, not a
     category filter. */
  const filtered = useMemo<PromptRow[]>(() => {
    const rows = (list.data ?? []).filter(
      (p) => Number(p.promptStatus) === 0,
    );
    const q = query.trim().toLowerCase();
    const byCategory = rows.filter((p) =>
      category === 'all' ? true : p.promptCategory === category,
    );
    const bySearch = !q
      ? byCategory
      : byCategory.filter(
          (p) =>
            (p.fileName ?? '').toLowerCase().includes(q) ||
            (p.promptName ?? '').toLowerCase().includes(q) ||
            (p.username ?? '').toLowerCase().includes(q) ||
            (p.remarks ?? '').toLowerCase().includes(q),
        );
    return sortPromptsNewestFirst(bySearch);
  }, [list.data, query, category]);

  /* ---- Pagination ---- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [query, category, pageSize]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  /* ---- Mutations ---- */
  const approve = useApprovePrompt();
  const reject = useRejectPrompt();
  const del = useDeletePrompt();
  const pausePrompt = usePausePrompt();
  const resumePrompt = useResumePrompt();
  const bulkDel = useBulkDeletePrompts();

  /* ---- Dialog state ---- */
  const [moderationTarget, setModerationTarget] = useState<{
    prompt: PromptRow;
    mode: 'approve' | 'reject';
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PromptRow | null>(null);
  const [transcribeTarget, setTranscribeTarget] = useState<PromptRow | null>(
    null,
  );

  /* ---- Selection ---- */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggleSelect = (id: number, next: boolean) =>
    setSelected((prev) => {
      const out = new Set(prev);
      if (next) out.add(id);
      else out.delete(id);
      return out;
    });
  const clearSelection = () => setSelected(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const handleModerate = (remarks: string) => {
    if (!moderationTarget) return;
    const { prompt, mode } = moderationTarget;
    const m = mode === 'approve' ? approve : reject;
    m.mutate(
      { promptId: prompt.promptId, remarks },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              (mode === 'approve' ? 'Approved' : 'Rejected'),
          );
          setModerationTarget(null);
        },
        onError: () =>
          toast.error(
            `Could not ${mode === 'approve' ? 'approve' : 'reject'} the prompt.`,
          ),
      },
    );
  };

  const handlePause = (prompt: PromptRow) => {
  pausePrompt.mutate(prompt.promptId, {
    onSuccess: (res) => toast.success(res?.message ?? 'Voice file paused'),
    onError: () => toast.error('Could not pause the voice file.'),
  });
};

const handleResume = (prompt: PromptRow) => {
  resumePrompt.mutate(prompt.promptId, {
    onSuccess: (res) => toast.success(res?.message ?? 'Voice file resumed'),
    onError: () => toast.error('Could not resume the voice file.'),
  });
};

  const canViewPendingList = usePermission('Voice Library.Prompts for Approval', BASE_PERMISSION.VIEW);

  // Only superadmin / sub-superadmin may see the pending-approval queue,
  // and only with the matching ACL grant.
  if (!isSubOrSuper || !canViewPendingList) return <Navigate to="/voice-files" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Pending IVR Prompts"
        description="Review user-submitted IVR prompts before they go live in campaigns."
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <PromptFilterBar
          query={query}
          onQueryChange={setQuery}
          category={category}
          onCategoryChange={setCategory}
          total={filtered.length}
          selectedCount={selected.size}
          onClearSelection={clearSelection}
          onBulkDelete={canDeletePending ? () => setBulkConfirm(true) : undefined}
          bulkBusy={bulkDel.isPending}
        />

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <PromptList
          prompts={paged}
          isLoading={list.isLoading}
          selectedIds={selected}
          onSelectChange={toggleSelect}
          onSelectAll={(next) => {
            if (next) setSelected(new Set(paged.map((p) => p.promptId)));
            else clearSelection();
          }}
          showApproveActions={canModerate}
          onApprove={
            canModerate
              ? (p) => setModerationTarget({ prompt: p, mode: 'approve' })
              : undefined
          }
          onReject={
            canModerate
              ? (p) => setModerationTarget({ prompt: p, mode: 'reject' })
              : undefined
          }
          onPause={handlePause}  
          onResume={handleResume}
          onDelete={canDeletePending ? (p) => setPendingDelete(p) : undefined}
          onCopyUrl={(p) => {
            if (!p.promptUrl) return;
            void navigator.clipboard.writeText(p.promptUrl);
            toast.success('Audio URL copied');
          }}
          onDownload={(p) => {
            if (!p.promptUrl) return;
            downloadPromptAudio(p.promptUrl, p.fileName);
          }}
          onTranscribe={canTranscribe ? (p) => setTranscribeTarget(p) : undefined}
          emptyTitle="Nothing waiting for review"
          emptyDescription="All submitted prompts have been moderated."
        />
        </div>

        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      <PromptModerationDialog
        prompt={moderationTarget?.prompt ?? null}
        mode={moderationTarget?.mode ?? 'approve'}
        isPending={approve.isPending || reject.isPending}
        onOpenChange={(o) => !o && setModerationTarget(null)}
        onConfirm={handleModerate}
      />

      <TranscribeDialog
        prompt={transcribeTarget}
        onOpenChange={(o) => !o && setTranscribeTarget(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete pending IVR prompt?"
        description={
          <>
            <span className="font-medium text-foreground">
              {pendingDelete?.fileName}
            </span>{' '}
            from {pendingDelete?.username ?? `user #${pendingDelete?.userId}`}{' '}
            will be removed without notifying the submitter.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.promptId, {
            onSuccess: () => {
              toast.success('Deleted');
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the prompt.'),
          });
        }}
      />

      <ConfirmDialog
        open={bulkConfirm}
        onOpenChange={setBulkConfirm}
        title="Delete selected pending prompts?"
        description={`${selected.size} pending IVR prompt${selected.size === 1 ? '' : 's'} will be removed.`}
        confirmLabel="Delete all"
        tone="destructive"
        loading={bulkDel.isPending}
        onConfirm={() => {
          bulkDel.mutate(
            {
              userId: Number(userId),
              promptIds: Array.from(selected).join(','),
            },
            {
              onSuccess: () => {
                toast.success(`Deleted ${selected.size} prompts`);
                clearSelection();
                setBulkConfirm(false);
              },
              onError: () => toast.error('Could not delete.'),
            },
          );
        }}
      />
    </FillPage>
  );
}
